import * as Sentry from '@sentry/react';

/**
 * F16 — Sentry init (frontend error reporting).
 *
 * Включается только если `VITE_SENTRY_DSN` задан (в dev обычно пусто).
 * Интеграции:
 *   - Captures unhandled errors (window.onerror, unhandledrejection).
 *   - Router integration: breadcrumbs для route changes.
 *   - Browser tracing (perf traces, sample 10%).
 *
 * Sentry событие дополняется полями:
 *   - `trace_id` (из response header `X-Trace-Id`, см. backend B10.7)
 *   - `user.id`, `user.email` если пользователь залогинен
 */
export function initSentry(): void {
  const dsn = import.meta.env['VITE_SENTRY_DSN'];
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env['MODE'] ?? 'production',
    release: import.meta.env['VITE_RELEASE'] ?? 'staiter-frontend@local',
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    replaysSessionSampleRate: 0.0, // только errors по умолчанию
    replaysOnErrorSampleRate: 0.2,
    beforeSend(event) {
      // Не шлём healthcheck 503 (шумит в алертах).
      const url = event.request?.url ?? '';
      if (url.includes('/readyz') || url.includes('/healthz')) return null;
      return event;
    },
  });
}

/**
 * Вспомогательный setUser — вызывается после `/me` hydration в `_auth.tsx`.
 */
export function setSentryUser(user: { id: string; email: string } | null): void {
  if (!import.meta.env['VITE_SENTRY_DSN']) return;
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture exception с trace_id.
 * Использовать в ErrorBoundary / telemetry-catch.
 */
export function captureError(err: unknown, context?: { trace_id?: string | undefined }): void {
  if (!import.meta.env['VITE_SENTRY_DSN']) return;
  Sentry.captureException(err, {
    tags: context?.trace_id ? { trace_id: context.trace_id } : undefined,
  });
}
